import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Crosshair, MapPin } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '../../lib/cn.js';

const ICON = { strokeWidth: 1.5 } as const;

/**
 * Onde a câmera abre quando a obra ainda não tem coordenada.
 *
 * Aripuanã, e não o centro do Brasil: o município é o caso de uso, e abrir num
 * ponto de Goiás obrigaria a arrastar o mapa meia tela antes do primeiro
 * clique. Município vizinho continua a um arrasto de distância.
 */
export const PADRAO: [number, number] = [-10.1666, -59.4569];
export const ZOOM_PADRAO = 12;
/** Perto o bastante para ver o canteiro, sem passar do que o OSM entrega. */
const ZOOM_ESCOLHIDO = 17;

/**
 * O marcador.
 *
 * O Leaflet monta o ícone padrão a partir de PNGs resolvidos por caminho
 * relativo ao CSS — que o empacotador reescreve, e o marcador some. Em vez de
 * remendar o caminho, o pino é desenhado aqui, em SVG, na cor do sistema.
 */
function pino(): L.DivIcon {
  /*
   * A cor vem do token, lida na hora: o SVG vai como texto para o Leaflet e
   * não enxerga variável de CSS, e o laranja muda de tom no tema escuro.
   */
  const primary =
    getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim() ||
    '#ff5a00';

  return L.divIcon({
    className: '',
    html: `<svg width="28" height="28" viewBox="0 0 24 24" fill="none"
      stroke="#ffffff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"
      style="filter: drop-shadow(0 1px 2px rgba(0,0,0,.45))">
      <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"
        fill="${primary}" />
      <circle cx="12" cy="10" r="3" fill="#ffffff" stroke="none" />
    </svg>`,
    iconSize: [28, 28],
    iconAnchor: [14, 27],
  });
}

/**
 * Escolhe a coordenada da obra no mapa.
 *
 * Ninguém digita latitude de cabeça, e o número que chega por WhatsApp vem
 * trocado de sinal ou com vírgula no lugar do ponto. Clicar no telhado da
 * escola é a forma como a informação existe na cabeça de quem fiscaliza.
 *
 * Os campos de texto continuam existindo ao lado, e mandam: quem tem a
 * coordenada do GPS a digita, e o mapa acompanha. O mapa é uma segunda entrada
 * para o mesmo dado, não a única.
 *
 * Os ladrilhos vêm do OpenStreetMap. Sem chave, sem conta e sem custo — e com a
 * atribuição que a licença exige, que o Leaflet já desenha no canto.
 */
export function MapaLocal({
  latitude,
  longitude,
  onChange,
  disabled = false,
}: {
  latitude: number | null;
  longitude: number | null;
  onChange: (latitude: number, longitude: number) => void;
  disabled?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapaRef = useRef<L.Map | null>(null);
  const marcadorRef = useRef<L.Marker | null>(null);
  /*
   * O callback muda a cada renderização do formulário. Guardado numa ref, o
   * mapa é montado uma vez só — reconstruí-lo a cada tecla digitada em outro
   * campo recarregaria os ladrilhos e piscaria a tela inteira.
   *
   * A troca acontece num efeito, e não durante a renderização: o React pode
   * descartar uma renderização pela metade, e a ref já teria sido escrita com
   * um callback de um estado que não chegou a existir.
   */
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const [erro, setErro] = useState<string | null>(null);
  const [localizando, setLocalizando] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || mapaRef.current) return;

    const mapa = L.map(container, {
      center: latitude != null && longitude != null ? [latitude, longitude] : PADRAO,
      zoom: latitude != null && longitude != null ? ZOOM_ESCOLHIDO : ZOOM_PADRAO,
      attributionControl: true,
      /*
       * A roda dá zoom, e não rolagem da página.
       *
       * É o comportamento que se espera de um mapa, e a etapa da localização é
       * curta o bastante para que prender a rolagem sobre o mapa não deixe
       * ninguém preso: o painel rola pelas bordas, e o corpo do modal tem
       * barra própria.
       */
      scrollWheelZoom: true,
    });

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap',
    }).addTo(mapa);

    mapa.on('click', (evento: L.LeafletMouseEvent) => {
      onChangeRef.current(
        Number(evento.latlng.lat.toFixed(6)),
        Number(evento.latlng.lng.toFixed(6)),
      );
    });

    mapaRef.current = mapa;

    /*
     * O mapa nasce dentro de um `<dialog>` que ainda está abrindo, e mede zero
     * de altura — os ladrilhos ficam cinza até alguém redimensionar a janela.
     * Um `invalidateSize` no quadro seguinte resolve, e o observador cobre a
     * troca de aba, que muda a altura do painel.
     */
    const observador = new ResizeObserver(() => mapa.invalidateSize());
    observador.observe(container);

    return () => {
      observador.disconnect();
      mapa.remove();
      mapaRef.current = null;
      marcadorRef.current = null;
    };
    // Só na montagem: a posição inicial já entrou acima, e o resto é o efeito abaixo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** A coordenada mandou; o mapa segue. Vale para o clique e para o digitado. */
  useEffect(() => {
    const mapa = mapaRef.current;
    if (!mapa) return;

    if (latitude == null || longitude == null) {
      marcadorRef.current?.remove();
      marcadorRef.current = null;
      return;
    }

    const posicao: [number, number] = [latitude, longitude];

    if (marcadorRef.current) {
      marcadorRef.current.setLatLng(posicao);
    } else {
      const marcador = L.marker(posicao, { icon: pino(), draggable: true }).addTo(mapa);

      marcador.on('dragend', () => {
        const { lat, lng } = marcador.getLatLng();
        onChangeRef.current(Number(lat.toFixed(6)), Number(lng.toFixed(6)));
      });

      marcadorRef.current = marcador;
    }

    // Só recentra se o ponto saiu de vista: senão o mapa pula a cada dígito.
    if (!mapa.getBounds().contains(posicao)) {
      mapa.setView(posicao, Math.max(mapa.getZoom(), ZOOM_ESCOLHIDO));
    }
  }, [latitude, longitude]);

  useEffect(() => {
    if (disabled) marcadorRef.current?.dragging?.disable();
    else marcadorRef.current?.dragging?.enable();
  }, [disabled, latitude, longitude]);

  function usarMinhaLocalizacao(): void {
    if (!navigator.geolocation) {
      setErro('Este navegador não disponibiliza serviço de localização.');
      return;
    }

    setErro(null);
    setLocalizando(true);

    navigator.geolocation.getCurrentPosition(
      (posicao) => {
        setLocalizando(false);
        onChangeRef.current(
          Number(posicao.coords.latitude.toFixed(6)),
          Number(posicao.coords.longitude.toFixed(6)),
        );
      },
      () => {
        setLocalizando(false);
        setErro(
          'Não foi possível obter a localização do dispositivo. Defina a posição no mapa ou informe as coordenadas.',
        );
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/*
       * Empilha no celular. Lado a lado numa tela de 390px, a instrução quebrava
       * em três linhas espremidas ao lado do botão.
       */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-center gap-1.5 text-[13px] text-ink-3">
          <MapPin {...ICON} size={14} aria-hidden />
          {latitude != null && longitude != null
            ? 'Arraste o marcador para ajustar a posição.'
            : 'Clique no mapa para definir as coordenadas da obra.'}
        </p>

        {/*
         * Um `<button>` de verdade, e não um controle do Leaflet: o formulário
         * está dentro de um `<form>`, e um botão sem `type` submeteria tudo.
         */}
        <button
          type="button"
          onClick={usarMinhaLocalizacao}
          disabled={disabled || localizando}
          className="flex h-8 shrink-0 items-center justify-center gap-1.5 self-start bg-sunken px-2.5 text-[13px] transition-colors hover:bg-sunken-2 disabled:opacity-50"
        >
          <Crosshair {...ICON} size={14} aria-hidden />
          {localizando ? 'Localizando…' : 'Usar localização do dispositivo'}
        </button>
      </div>

      <div
        ref={containerRef}
        role="application"
        aria-label="Mapa para escolher o local da obra"
        className={cn(
          'h-80 w-full overflow-hidden bg-sunken',
          disabled && 'pointer-events-none opacity-60',
        )}
      />

      {erro ? (
        <p role="alert" className="text-[12px] text-danger">
          {erro}
        </p>
      ) : null}
    </div>
  );
}
